import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, getToken } from "@/lib/api";
import { MonitorPlay, Check, X, ArrowRight, Loader2, Mail, Phone } from "lucide-react";

const PLANS = [
  {
    id: "3bulan",
    name: "3 Bulan",
    tagline: "Coba dulu, cocok baru lanjut",
    price: 80000,
    period: "3 bulan",
    monthly: "~Rp26.700 / bulan",
    screens: "1 Layar TV",
    features: [
      "Upload Gambar & Video",
      "Penjadwalan Konten",
      "Dashboard Online",
      "Support via WhatsApp",
    ],
  },
  {
    id: "6bulan",
    name: "6 Bulan",
    tagline: "Paling populer — harga terbaik",
    price: 130000,
    period: "6 bulan",
    monthly: "~Rp21.700 / bulan",
    screens: "1 Layar TV",
    popular: true,
    save: "Hemat Rp30.000",
    features: [
      "Upload Gambar & Video",
      "Penjadwalan Konten",
      "Dashboard Online",
      "Support via WhatsApp",
      "Laporan Bulanan",
    ],
  },
  {
    id: "1tahun",
    name: "1 Tahun",
    tagline: "Investasi jangka panjang",
    price: 250000,
    period: "1 tahun",
    monthly: "~Rp20.800 / bulan",
    screens: "1 Layar TV",
    save: "Hemat Rp50.000",
    features: [
      "Upload Gambar & Video",
      "Penjadwalan Konten",
      "Dashboard Online",
      "Support Prioritas",
      "Laporan Bulanan",
      "Free Setup Awal",
    ],
  },
];

export default function Harga() {
  const nav = useNavigate();
  const token = getToken();
  const [loading, setLoading] = useState(true);
  const [trialInfo, setTrialInfo] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!token) {
        if (mounted) setLoading(false);
        return;
      }
      try {
        const { data } = await api.get("/auth/trial-info");
        if (mounted) setTrialInfo(data);
      } catch {
        // ignore
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [token, nav]);

  const expired = trialInfo?.expired ?? false;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4F5]">
        <Loader2 className="h-6 w-6 text-[#002FA7] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      {/* ── Header ── */}
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-2 group">
            <img src="/logo.svg" alt="DigiSign" className="h-8" />
          </Link>
          <div className="flex items-center gap-4">
            <span className="label-eyebrow hidden sm:block">DigiSign.Web.ID</span>
            <Link to="/login" className="signage-btn-primary text-sm h-9 flex items-center gap-1.5">
              Sign in
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </header>

      {/* ── Trial Expired Banner ── */}
      {expired && (
        <div className="bg-red-50 border-b border-red-200">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-start gap-3">
            <div className="h-8 w-8 bg-red-100 rounded flex items-center justify-center shrink-0 mt-0.5">
              <X className="h-4 w-4 text-red-600" />
            </div>
            <div>
              <p className="font-semibold text-red-800 text-sm sm:text-base">
                ❌ Trial Expired
              </p>
              <p className="text-red-600 text-sm mt-0.5">
                Masa trial 7 hari Anda telah berakhir. Silakan pilih paket berlangganan di bawah untuk melanjutkan.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Hero ── */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-8 text-center">
        <p className="label-eyebrow mb-3 text-[#002FA7]">Harga</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-none mb-4">
          {expired ? "Pilih paket untuk melanjutkan" : "Terjangkau untuk Semua Skala Bisnis"}
        </h1>
        <p className="text-zinc-500 max-w-lg mx-auto text-sm sm:text-base">
          {expired
            ? "Masa trial telah berakhir. Pilih paket berlangganan untuk tetap menggunakan SIGNAGE/OS."
            : "Makin lama berlangganan, makin hemat per bulannya."}
        </p>
      </section>

      {/* ── Pricing Cards ── */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="grid md:grid-cols-3 gap-6 items-start">
          {PLANS.map((plan) => {
            const isPopular = plan.popular;
            return (
              <div
                key={plan.id}
                className={`relative bg-white border ${
                  isPopular ? "border-[#002FA7]" : "border-zinc-200"
                } ${isPopular ? "shadow-lg shadow-[#002FA7]/5" : "shadow-sm"}`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#002FA7] text-white text-[11px] font-bold uppercase tracking-widest px-4 py-1">
                    POPULER
                  </div>
                )}
                <div className="p-8">
                  <p className="label-eyebrow text-zinc-400 mb-1">{plan.tagline}</p>
                  <h3 className="font-display text-2xl font-bold tracking-tight">{plan.name}</h3>

                  <div className="mt-6 flex items-baseline gap-1">
                    <span className="font-display text-4xl font-black tracking-tight">
                      Rp{plan.price.toLocaleString("id-ID")}
                    </span>
                  </div>

                  <div className="mt-1 text-sm text-zinc-500">
                    {plan.period} — {plan.monthly}
                  </div>

                  {plan.save && (
                    <div className="mt-3 inline-block bg-emerald-50 text-emerald-700 text-xs font-bold px-3 py-1">
                      {plan.save}
                    </div>
                  )}

                  <div className="mt-4 flex items-center gap-2 text-sm text-zinc-500">
                    <MonitorPlay className="h-4 w-4" />
                    <span>{plan.screens}</span>
                  </div>

                  <div className="my-5 h-px bg-zinc-100" />

                  <ul className="space-y-3">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm">
                        <Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => nav(`/payment?plan=${plan.id}`)}
                    className={`mt-8 w-full h-11 flex items-center justify-center gap-2 text-sm font-medium transition-all ${
                      isPopular
                        ? "signage-btn-primary disabled:opacity-60"
                        : "signage-btn-secondary disabled:opacity-60"
                    }`}
                  >
                    Pilih Paket
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Contact ── */}
      <section className="border-t border-zinc-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h2 className="font-display text-2xl font-bold tracking-tight mb-4">
            Ada pertanyaan? Hubungi kami
          </h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-sm">
            <a
              href="mailto:sales@digisign.web.id"
              className="flex items-center gap-2 text-zinc-600 hover:text-[#002FA7] transition-colors"
            >
              <Mail className="h-4 w-4" />
              sales@digisign.web.id
            </a>
            <a
              href="tel:+6285962658062"
              className="flex items-center gap-2 text-zinc-600 hover:text-[#002FA7] transition-colors"
            >
              <Phone className="h-4 w-4" />
              +62 859-6265-8062
            </a>
          </div>
          <p className="mt-6 text-xs text-zinc-400 max-w-lg mx-auto">
            *Harga belum termasuk PPN 11%.
          </p>
        </div>
      </section>

      <footer className="bg-zinc-900 text-zinc-400 py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo.svg" alt="DigiSign" className="h-6" />
          </div>
          <p className="text-xs">
            &copy; {new Date().getFullYear()} DigiSign.Web.ID — All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
